// src/pages/projects/ProjectCompaniesPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    listProjectCompanies,
    createProjectCompany,
    updateProjectCompany,
    deleteProjectCompany,
    type ProjectCompanyItem,
    setProjectCompanyRepresentative,
} from 'src/features/projects/companiesApi';
import { type ProjectCompanyRole, PROJECT_COMPANY_ROLE_OPTIONS } from 'src/features/company/types';

import { type CompanyPartner } from 'src/features/company/api';
import { listCompanyPartners } from 'src/features/projects/api';

import ProjectCompanyRepresentativeModal from 'src/features/projects/ProjectCompanyRepresentativeModal';
import { useToast } from 'src/shared/ui/toast/useToast';

type Row = ProjectCompanyItem & {
    editing?: boolean;
    editRole?: ProjectCompanyRole;
    editContractAmount?: string;
    editProgressPercent?: string;
};

type PartnerCompany = CompanyPartner;

const ProjectCompaniesPage: React.FC = () => {
    const params = useParams();
    const projectId = params.projectId as string;
    const toast = useToast();

    const [rows, setRows] = useState<Row[]>([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const [partnerCompanies, setPartnerCompanies] = useState<PartnerCompany[]>([]);

    const [newPartnerCompanyId, setNewPartnerCompanyId] = useState('');
    const [newRole, setNewRole] = useState<ProjectCompanyRole>('SUB_CONTRACTOR');
    const [newContractAmount, setNewContractAmount] = useState('');
    const [creating, setCreating] = useState(false);

    const [savingId, setSavingId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const [repTarget, setRepTarget] = useState<Row | null>(null);
    const [repModalOpen, setRepModalOpen] = useState(false);

    const reload = async () => {
        if (!projectId) return;

        setLoading(true);
        setErr(null);

        try {
            const [projectCompanies, partnerCompanyList] = await Promise.all([
                listProjectCompanies(projectId),
                listCompanyPartners(projectId),
            ]);

            setRows(
                projectCompanies.map((pc) => ({
                    ...pc,
                    editing: false,
                    editRole: pc.role,
                    editContractAmount: pc.contractAmount ?? '',
                    editProgressPercent: pc.progressPercent ?? '',
                })),
            );

            setPartnerCompanies(partnerCompanyList);
        } catch (e: any) {
            setErr(e?.response?.data?.error?.message ?? '프로젝트 참여 회사 목록을 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void reload();
    }, [projectId]);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!projectId) return;

        if (!newPartnerCompanyId) {
            toast.warning('회사를 선택해 주세요.', {
                title: '입력 필요',
            });
            return;
        }

        setCreating(true);
        setErr(null);

        try {
            const created = await createProjectCompany({
                projectId,
                companyId: newPartnerCompanyId,
                role: newRole,
                contractAmount: newContractAmount || undefined,
            });

            setRows((prev) => [
                {
                    ...created,
                    editing: false,
                    editRole: created.role,
                    editContractAmount: created.contractAmount ?? '',
                    editProgressPercent: created.progressPercent ?? '',
                },
                ...prev,
            ]);

            setNewPartnerCompanyId('');
            setNewRole('SUB_CONTRACTOR');
            setNewContractAmount('');

            toast.success(`${created.company.name} 회사를 프로젝트에 추가했습니다.`, {
                title: '추가 완료',
            });
        } catch (e: any) {
            const msg = e?.response?.data?.error?.message ?? '프로젝트 참여 회사 추가 중 오류가 발생했습니다.';
            setErr(msg);
            toast.error(msg, {
                title: '추가 실패',
            });
        } finally {
            setCreating(false);
        }
    };

    const startEdit = (id: string) => {
        setRows((prev) =>
            prev.map((r) =>
                r.id === id
                    ? {
                          ...r,
                          editing: true,
                          editRole: r.role,
                          editContractAmount: r.contractAmount ?? '',
                          editProgressPercent: r.progressPercent ?? '',
                      }
                    : r,
            ),
        );
    };

    const cancelEdit = (id: string) => {
        setRows((prev) =>
            prev.map((r) =>
                r.id === id
                    ? {
                          ...r,
                          editing: false,
                          editRole: r.role,
                          editContractAmount: r.contractAmount ?? '',
                          editProgressPercent: r.progressPercent ?? '',
                      }
                    : r,
            ),
        );
    };

    const saveEdit = async (id: string) => {
        if (!projectId) return;

        const row = rows.find((r) => r.id === id);
        if (!row) return;

        setSavingId(id);

        try {
            const updated = await updateProjectCompany({
                projectId,
                id,
                role: row.editRole,
                contractAmount: row.editContractAmount,
                progressPercent: row.editProgressPercent,
            });

            setRows((prev) =>
                prev.map((r) =>
                    r.id === id
                        ? {
                              ...updated,
                              editing: false,
                              editRole: updated.role,
                              editContractAmount: updated.contractAmount ?? '',
                              editProgressPercent: updated.progressPercent ?? '',
                          }
                        : r,
                ),
            );

            toast.success(`${updated.company.name} 정보를 수정했습니다.`, {
                title: '수정 완료',
            });
        } catch (e: any) {
            toast.error(e?.response?.data?.error?.message ?? '프로젝트 참여 회사 수정 중 오류가 발생했습니다.', {
                title: '수정 실패',
            });
        } finally {
            setSavingId(null);
        }
    };

    const handleDelete = async (id: string) => {
        if (!projectId) return;

        const target = rows.find((r) => r.id === id);

        if (!window.confirm('이 회사를 이 프로젝트에서 제거하시겠습니까?')) return;

        setDeletingId(id);

        try {
            await deleteProjectCompany(projectId, id);
            setRows((prev) => prev.filter((r) => r.id !== id));

            toast.success(`${target?.company.name ?? '회사'}를 프로젝트에서 제거했습니다.`, {
                title: '제거 완료',
            });
        } catch (e: any) {
            toast.error(e?.response?.data?.error?.message ?? '프로젝트 참여 회사 삭제 중 오류가 발생했습니다.', {
                title: '제거 실패',
            });
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="p-4 space-y-4">
            <div>
                <h2 className="text-sm font-semibold text-gray-900">Project Companies</h2>
                <p className="mt-1 text-xs text-gray-500">
                    이 프로젝트에 참여하는 회사(발주처, 하도급, PMC, QC, 공급사 등)를 관리합니다. 회사는 CompanyAdmin이
                    미리 등록한 파트너 회사 목록에서 선택합니다.
                </p>
            </div>

            {err && (
                <div className="text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">
                    {err}
                </div>
            )}

            <form
                onSubmit={handleCreate}
                className="flex flex-wrap items-end gap-2 border border-gray-200 rounded-xl px-3 py-3 bg-gray-50/60"
            >
                <div className="min-w-[200px] flex-1 space-y-1">
                    <label className="block text-xs font-medium text-gray-700">파트너 회사</label>
                    <select
                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-sky-500"
                        value={newPartnerCompanyId}
                        onChange={(e) => setNewPartnerCompanyId(e.target.value)}
                        disabled={creating}
                    >
                        <option value="">회사 선택</option>
                        {partnerCompanies.map((company) => (
                            <option key={company.id} value={company.id}>
                                {company.name} {company.type ? `(${company.type})` : ''}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="w-40 space-y-1">
                    <label className="block text-xs font-medium text-gray-700">프로젝트 역할</label>
                    <select
                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-sky-500"
                        value={newRole}
                        onChange={(e) => setNewRole(e.target.value as ProjectCompanyRole)}
                        disabled={creating}
                    >
                        {PROJECT_COMPANY_ROLE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="w-40 space-y-1">
                    <label className="block text-xs font-medium text-gray-700">계약금액</label>
                    <input
                        type="number"
                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-sky-500 text-right tabular-nums"
                        placeholder="예: 100000000"
                        value={newContractAmount}
                        onChange={(e) => setNewContractAmount(e.target.value)}
                        disabled={creating}
                    />
                </div>

                <div className="flex justify-end">
                    <button
                        type="submit"
                        className="px-3 py-1.5 rounded-full bg-sky-700 text-white text-xs hover:bg-sky-800 disabled:opacity-60"
                        disabled={creating}
                    >
                        {creating ? '추가 중…' : '회사 추가'}
                    </button>
                </div>
            </form>

            <div className="border-t border-gray-200" />

            <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                    <thead>
                        <tr className="bg-gray-50 text-xs text-gray-500">
                            <th className="px-3 py-2 font-medium">회사명</th>
                            <th className="px-3 py-2 font-medium">프로젝트 역할</th>
                            <th className="px-3 py-2 font-medium">대표자</th>
                            <th className="px-3 py-2 font-medium text-right">계약금액</th>
                            <th className="px-3 py-2 font-medium text-right">진도율 (%)</th>
                            <th className="px-3 py-2 font-medium text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="text-xs">
                        {loading && (
                            <tr>
                                <td colSpan={6} className="px-3 py-4 text-center text-gray-400">
                                    로딩 중…
                                </td>
                            </tr>
                        )}

                        {!loading && rows.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-3 py-4 text-center text-gray-400">
                                    이 프로젝트에 등록된 회사가 없습니다. 위 폼을 사용하여 회사를 추가해 주세요.
                                </td>
                            </tr>
                        )}

                        {!loading &&
                            rows.map((r) => (
                                <tr key={r.id} className="border-t">
                                    <td className="px-3 py-2">
                                        {r.company.name}{' '}
                                        {r.company.type ? (
                                            <span className="text-[10px] text-gray-400">({r.company.type})</span>
                                        ) : null}
                                    </td>

                                    <td className="px-3 py-2">
                                        {r.editing ? (
                                            <select
                                                className="border border-gray-300 rounded-md px-2 py-1 text-xs bg-white"
                                                value={r.editRole}
                                                onChange={(e) =>
                                                    setRows((prev) =>
                                                        prev.map((x) =>
                                                            x.id === r.id
                                                                ? {
                                                                      ...x,
                                                                      editRole: e.target.value as ProjectCompanyRole,
                                                                  }
                                                                : x,
                                                        ),
                                                    )
                                                }
                                                disabled={savingId === r.id}
                                            >
                                                {PROJECT_COMPANY_ROLE_OPTIONS.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            r.role
                                        )}
                                    </td>

                                    <td className="px-3 py-2">
                                        {r.representativeMember ? (
                                            <div className="flex flex-col">
                                                <span className="text-xs text-gray-900">
                                                    {r.representativeMember.user.name ||
                                                        r.representativeMember.user.email}
                                                </span>
                                                <span className="text-[10px] text-gray-400">
                                                    {r.representativeMember.user.email}
                                                </span>
                                            </div>
                                        ) : (
                                            <span className="text-[11px] text-gray-400">미지정</span>
                                        )}
                                    </td>

                                    <td className="px-3 py-2 text-right tabular-nums">
                                        {r.editing ? (
                                            <input
                                                type="number"
                                                className="w-24 border border-gray-300 rounded-md px-2 py-1 text-xs text-right"
                                                value={r.editContractAmount ?? ''}
                                                onChange={(e) =>
                                                    setRows((prev) =>
                                                        prev.map((x) =>
                                                            x.id === r.id
                                                                ? { ...x, editContractAmount: e.target.value }
                                                                : x,
                                                        ),
                                                    )
                                                }
                                                disabled={savingId === r.id}
                                            />
                                        ) : r.contractAmount ? (
                                            Number(r.contractAmount).toLocaleString()
                                        ) : (
                                            '-'
                                        )}
                                    </td>

                                    <td className="px-3 py-2 text-right tabular-nums">
                                        {r.editing ? (
                                            <input
                                                type="number"
                                                step="0.01"
                                                className="w-16 border border-gray-300 rounded-md px-2 py-1 text-xs text-right"
                                                value={r.editProgressPercent ?? ''}
                                                onChange={(e) =>
                                                    setRows((prev) =>
                                                        prev.map((x) =>
                                                            x.id === r.id
                                                                ? { ...x, editProgressPercent: e.target.value }
                                                                : x,
                                                        ),
                                                    )
                                                }
                                                disabled={savingId === r.id}
                                            />
                                        ) : r.progressPercent != null ? (
                                            Number(r.progressPercent).toFixed(2)
                                        ) : (
                                            '-'
                                        )}
                                    </td>

                                    <td className="px-3 py-2 text-right space-x-2">
                                        <button
                                            type="button"
                                            className="text-xs text-sky-700 hover:underline"
                                            onClick={() => {
                                                setRepTarget(r);
                                                setRepModalOpen(true);
                                            }}
                                        >
                                            대표자 설정
                                        </button>

                                        {r.editing ? (
                                            <>
                                                <button
                                                    type="button"
                                                    className="text-xs text-gray-600 hover:underline disabled:opacity-60"
                                                    onClick={() => cancelEdit(r.id)}
                                                    disabled={savingId === r.id}
                                                >
                                                    취소
                                                </button>
                                                <button
                                                    type="button"
                                                    className="text-xs text-sky-700 hover:underline disabled:opacity-60"
                                                    onClick={() => saveEdit(r.id)}
                                                    disabled={savingId === r.id}
                                                >
                                                    저장
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    className="text-xs text-gray-600 hover:underline"
                                                    onClick={() => startEdit(r.id)}
                                                >
                                                    수정
                                                </button>
                                                <button
                                                    type="button"
                                                    className="text-xs text-rose-600 hover:underline disabled:opacity-60"
                                                    onClick={() => handleDelete(r.id)}
                                                    disabled={deletingId === r.id}
                                                >
                                                    제거
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>

            {projectId && (
                <ProjectCompanyRepresentativeModal
                    open={repModalOpen}
                    onClose={() => setRepModalOpen(false)}
                    projectId={projectId}
                    projectCompany={repTarget}
                    onSaved={(updated) => {
                        setRows((prev) =>
                            prev.map((r) => (r.id === updated.id ? { ...updated, editing: r.editing } : r)),
                        );
                    }}
                    onSubmit={setProjectCompanyRepresentative}
                />
            )}
        </div>
    );
};

export default ProjectCompaniesPage;
